import React, { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getScrollers } from './content';

const ResponsiveMenu = ({ isMobile }) => {
  const [isOpen, setIsOpen] = useState(false);
    const navRef = useRef(null);

  // The motif is red on the pale pages and white on the dark ones. This reads
  // the background under the button and sets .bars-dark or .bars-light.
  //
  // Earlier versions of this appeared not to work. They did: the class was
  // changing correctly all along, and a rule with !important further up the
  // stylesheet was overriding the colour. That rule is gone.
  const [barTone, setBarTone] = useState('light');

  useEffect(() => {
    let queued = false;

    const brightness = (colour) => {
      const m = colour && colour.match(/rgba?\(([^)]+)\)/);
      if (!m) return null;
      const parts = m[1].split(',').map((n) => parseFloat(n));
      if (parts.length > 3 && parts[3] < 0.5) return null;   // see-through
      const [r, g, b] = parts;
      return (r * 299 + g * 587 + b * 114) / 1000;           // 0 dark, 255 light
    };

    const read = () => {
      queued = false;
      const x = Math.max(40, window.innerWidth * 0.86);
      for (const el of document.elementsFromPoint(x, 34) || []) {
        if (!(el instanceof HTMLElement)) continue;
        if (el.dataset && el.dataset.tone) {
          setBarTone(el.dataset.tone === 'dark' ? 'light' : 'dark');
          return;
        }
        const cs = window.getComputedStyle(el);
        if (el.tagName === 'VIDEO' || el.tagName === 'IMG' ||
            (cs.backgroundImage && cs.backgroundImage !== 'none')) {
          setBarTone('light');
          return;
        }
        const lum = brightness(cs.backgroundColor);
        if (lum !== null) { setBarTone(lum < 150 ? 'light' : 'dark'); return; }
      }
      setBarTone('dark');
    };

    const onScroll = () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(read);
    };

    read();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const toggleMenu = () => {
    const next = !isOpen;
    setIsOpen(next);
    // Let anything playing on the page know the menu has covered it.
    if (next) {
      window.dispatchEvent(new CustomEvent('menu:open'));
    }
  };

  // Close when clicking anywhere outside the panel, or on Escape.
  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointer = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKey = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  const menuItems = [
    { label: 'Watch Film', href: '/#watch' },
    { label: 'Screenings', href: '/screenings' },
    { label: 'Be Aware', href: '/climate-awareness' },
    { label: 'Climate Resilience', href: '/climate-resilience' },
    { label: 'Contact', href: '/#contact' },
  ];

  return (
    <nav ref={navRef} className={`responsive-menu ${isMobile ? 'mobile' : ''} ${isOpen ? 'open' : ''} bars-${barTone}`}>
      <button className="hamburger-button" onClick={toggleMenu} aria-label={isOpen ? 'Close menu' : 'Open menu'} aria-expanded={isOpen}>
        {/* The kira motif stands in for the three bars while the menu is
            closed. It carries both colours and the stylesheet shows whichever
            reads against the band behind it. Open, it becomes the usual
            cross. */}
        <img className="menu-motif menu-motif--red" src="/assets/gap/kira-motif-dorji-jadram.png" alt="" aria-hidden="true" width="79" height="90" />
        <img className="menu-motif menu-motif--white" src="/assets/gap/kira-motif-dorji-jadram-white.png" alt="" aria-hidden="true" width="79" height="90" />
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
      </button>
      {isOpen && (
        <div className="full-screen-menu">
          <div className='menu-logo'>
            <a href='/' onClick={() => setIsOpen(false)} aria-label='The Girl and Her Piano - home'>
              <img src='/assets/gap/the-girl-and-her-piano-wordmark-blue.png' alt='The Girl and Her Piano' />
            </a>
          </div>
          <ul className="menu-items">
            {menuItems.map((item, index) => (
              <li key={index}>
                <a href={item.href} onClick={() => setIsOpen(false)}>
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
          <div className='social-icons'>
          <a href='mailto:info@thegirlandherpiano.com' aria-label='Email' className='social-icon'>
            <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
              <path d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z'></path>
              <polyline points='22,6 12,13 2,6'></polyline>
            </svg>
          </a>
          <a href='https://www.imdb.com/title/tt37798371/' target='_blank' rel='noopener noreferrer' aria-label='IMDb' className='social-icon social-icon--imdb'>IMDb</a>
          <a href='https://www.instagram.com/thegirlandherpiano' target='_blank' rel='noopener noreferrer' aria-label='Instagram' className='social-icon'>
              <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <rect x='2' y='2' width='20' height='20' rx='5' ry='5'></rect>
                <path d='M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z'></path>
                <line x1='17.5' y1='6.5' x2='17.51' y2='6.5'></line>
              </svg>
            </a>
            <a href='https://www.youtube.com/@hillaryosullivan' target='_blank' rel='noopener noreferrer' aria-label='YouTube' className='social-icon social-icon--youtube'>
              <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='currentColor'>
                <path d='M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31.4 31.4 0 0 0 0 12a31.4 31.4 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31.4 31.4 0 0 0 24 12a31.4 31.4 0 0 0-.5-5.8zM9.6 15.6V8.4l6.3 3.6z'></path>
              </svg>
            </a>
            <a href='https://www.facebook.com/thegirlandherpiano' target='_blank' rel='noopener noreferrer' aria-label='Facebook' className='social-icon'>
              <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='currentColor' stroke='currentColor' strokeWidth='0.5' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z'></path>
              </svg>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

function App() {
  const dispatch = useDispatch();

  // These are read once, synchronously, before the first paint. The earlier
  // version started every flag at false and only corrected them inside an
  // effect, so a phone rendered the desktop layout first and then jumped to
  // the mobile one. That jump was the whole of the page's layout shift: the
  // hero title block is 82svh on desktop and 62svh on a phone, so everything
  // below it moved. Starting from the real values means nothing moves.
  const readDevice = () => {
    if (typeof window === 'undefined') {
      return { mobileDevice: false, smallScreen: false, landscape: false }
    }
    const ua = window.navigator.userAgent.toLowerCase()
    const keywords = ['android', 'webos', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone']
    return {
      mobileDevice: keywords.some((k) => ua.includes(k)),
      smallScreen: window.innerWidth <= 768,
      landscape: window.innerWidth > window.innerHeight,
    }
  }

  const first = readDevice()
  const [isMobile, setIsMobile] = useState(first.mobileDevice || first.smallScreen)
  const [isLandscape, setIsLandscape] = useState(first.landscape)
  const [isSmallScreen, setIsSmallScreen] = useState(first.smallScreen)
  const [isMobileDevice, setIsMobileDevice] = useState(first.mobileDevice)

  useEffect(() => {
    const checkIfMobile = () => {
      // Read into locals and use those. The earlier version passed the state
      // variables to setIsMobile, which were still the previous render's
      // values, and then listed them as dependencies to paper over it.
      const { mobileDevice, smallScreen, landscape } = readDevice()
      setIsMobileDevice(mobileDevice)
      setIsSmallScreen(smallScreen)
      setIsMobile(mobileDevice || smallScreen)
      setIsLandscape(landscape)
      document.body.classList.toggle('mobile', mobileDevice || smallScreen)
      document.body.classList.toggle('landscape', landscape)
    }

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    window.addEventListener('orientationchange', checkIfMobile);

    return () => {
      window.removeEventListener('resize', checkIfMobile);
      window.removeEventListener('orientationchange', checkIfMobile);
    };
  }, []);

  useEffect(() => {
    dispatch(getScrollers());
  }, [dispatch]);

  return (
    <div className="App">
      <ResponsiveMenu isMobile={isMobile} />
      <Outlet context={{ isMobile, isLandscape, isMobileDevice, isSmallScreen }} />
    </div>
  );
}

export default App;